import React from 'react';
import { Box, Typography } from '@mui/material';
import { DecimalNumberInput } from '../../../../components/ui';
import { SelectedProduct } from '../../../../types/sales.types';
import { formatCurrency } from '../../../../utils/formatters/money';
import { roundMoney } from '../../../../utils/money';
import {
  hasLineDiscount,
  hasLineMarkup,
  isLineDiscountInvalid,
  isZeroPriceLine,
  linePricingDisplayBase,
} from '../../../../utils/sales/line-pricing.utils';
import { isRemoteCartItem } from '../../../../utils/sales/mixed-sale.utils';
import ProductImagePreview, { ProductImageData } from '../ProductImagePreview';
import RemoteOriginChip from '../RemoteOriginChip';
import AdjustmentTypeToggle from '../AdjustmentTypeToggle';
import * as S from '../../SalesStep2/SalesStep2.styles';

interface SalesLinePricingCardProps {
  product: SelectedProduct;
  /** Selling branch id — shows yellow origin chip for remote pieces */
  sellingLocationId?: number;
  editable: boolean;
  showMarkupControls: boolean;
  showIndividualMarkupControls: boolean;
  showDiscountControls: boolean;
  showIndividualDiscountControls: boolean;
  onMarkupChange: (
    productId: number,
    markupType: 'PERCENTAGE' | 'AMOUNT',
    markupValue: number,
  ) => void;
  onDiscountChange: (
    productId: number,
    discountType: 'PERCENTAGE' | 'AMOUNT',
    discountValue: number,
  ) => void;
  formatAdjustmentDisplay: (type: string, value: number, basePrice: number) => string;
}

const getProductImageData = (product: ProductImageData): ProductImageData => ({
  thumbnail: product.thumbnail ?? null,
  thumbnailUrl: product.thumbnailUrl ?? null,
  imagePath: product.imagePath ?? null,
});

const compactInputSx = {
  width: 76,
  '& .MuiInputBase-root': { fontSize: '0.75rem' },
  '& .MuiFormHelperText-root': { display: 'none' },
};

const lineControlsSx = {
  mt: 0.75,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  flexWrap: 'wrap' as const,
};

const controlLabelSx = {
  fontSize: '0.7rem',
  fontWeight: 600,
  flexShrink: 0,
};

const SalesLinePricingCard: React.FC<SalesLinePricingCardProps> = ({
  product,
  sellingLocationId,
  editable,
  showMarkupControls,
  showIndividualMarkupControls,
  showDiscountControls,
  showIndividualDiscountControls,
  onMarkupChange,
  onDiscountChange,
  formatAdjustmentDisplay,
}) => {
  const productId = product.warehouseInventoryProduct.id;
  const isRemote = isRemoteCartItem(product, sellingLocationId);
  const originName = product.warehouseInventoryProduct.location?.name;
  const displayBase = linePricingDisplayBase(product);
  // Sin precio cargado: el precio se fija con un incremento por pieza en Bs.
  const isZeroBase = isZeroPriceLine(displayBase);
  const markupAmount = roundMoney(Math.max(0, product.adjustedPrice - displayBase));
  const discountAmount = roundMoney(Math.max(0, product.adjustedPrice - product.finalPrice));
  const hasMarkup = hasLineMarkup(product.markupType, product.markupValue) && markupAmount > 0;
  const hasDiscount =
    hasLineDiscount(product.discountType, product.discountValue) && discountAmount > 0;
  const discountInvalid = isLineDiscountInvalid(
    product.adjustedPrice,
    product.discountType,
    product.discountValue,
  );
  const showLineMarkupControls = showMarkupControls && showIndividualMarkupControls;
  const showLineDiscountControls = showDiscountControls && showIndividualDiscountControls;
  const showLineControls = editable && (showLineMarkupControls || showLineDiscountControls);

  let zeroBaseHint: string | null = null;
  if (isZeroBase) {
    if (showLineControls && showLineMarkupControls && product.markupType === 'PERCENTAGE') {
      zeroBaseHint = 'Sobre Bs 0 el % no tiene efecto: use un monto en Bs.';
    } else if (product.finalPrice <= 0) {
      zeroBaseHint =
        showLineControls && showLineMarkupControls
          ? 'Sin precio cargado: use un incremento en Bs para fijar su precio.'
          : 'Sin precio cargado (Bs 0)';
    }
  }

  // Amount markup/discount labels follow stored adjusted/final so they match the order summary.
  const markupSummary = hasMarkup
    ? `+${formatAdjustmentDisplay(
        product.markupType,
        product.markupType === 'PERCENTAGE'
          ? product.markupValue
          : roundMoney(Math.max(0, product.adjustedPrice - displayBase)),
        displayBase,
      )}`
    : null;
  const discountSummary = hasDiscount
    ? `−${formatAdjustmentDisplay(
        product.discountType,
        product.discountType === 'PERCENTAGE'
          ? product.discountValue
          : roundMoney(Math.max(0, product.adjustedPrice - product.finalPrice)),
        product.adjustedPrice,
      )}`
    : null;

  return (
    <S.PricingLineCard>
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <ProductImagePreview
          product={getProductImageData(
            product.warehouseInventoryProduct.product as ProductImageData,
          )}
          alt={product.warehouseInventoryProduct.product.name}
          size={48}
          borderRadius={8}
          expandable={false}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: '0.875rem' }}
                noWrap
              >
                {product.warehouseInventoryProduct.product.name}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  flexWrap: 'wrap',
                  mt: 0.15,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {product.warehouseInventoryProduct.productCodeUnique ?? 'N/A'}
                </Typography>
                {isRemote && <RemoteOriginChip locationName={originName} />}
              </Box>
            </Box>
            <Typography
              variant="body2"
              color="primary.main"
              sx={{ fontWeight: 700, flexShrink: 0, fontSize: '0.9375rem' }}
            >
              {formatCurrency(product.finalPrice)}
            </Typography>
          </Box>

          {(hasMarkup || hasDiscount) && (
            <Typography
              variant="caption"
              component="div"
              sx={{ display: 'block', mt: 0.35, fontSize: '0.75rem', lineHeight: 1.35 }}
            >
              <Box component="span" color="text.secondary">
                {formatCurrency(displayBase)}
              </Box>
              {markupSummary != null && (
                <>
                  <Box component="span" color="text.secondary">
                    {' · '}
                  </Box>
                  <Box component="span" color="success.main">
                    {markupSummary}
                  </Box>
                </>
              )}
              {discountSummary != null && (
                <>
                  <Box component="span" color="text.secondary">
                    {' · '}
                  </Box>
                  <Box component="span" color="error.main">
                    {discountSummary}
                  </Box>
                </>
              )}
            </Typography>
          )}

          {zeroBaseHint != null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.35, fontSize: '0.75rem' }}
            >
              {zeroBaseHint}
            </Typography>
          )}

          {showLineControls && (
            <Box sx={lineControlsSx}>
              {showLineMarkupControls && (
                <>
                  <Typography variant="caption" color="success.main" sx={controlLabelSx}>
                    Incremento
                  </Typography>
                  <AdjustmentTypeToggle
                    value={product.markupType === 'PERCENTAGE' ? 'PERCENTAGE' : 'AMOUNT'}
                    disabled={!editable}
                    onChange={(type) => onMarkupChange(productId, type, product.markupValue)}
                    ariaLabel="Tipo de incremento de la pieza"
                  />
                  <DecimalNumberInput
                    size="small"
                    value={product.markupValue > 0 ? product.markupValue : ''}
                    onChange={(val) =>
                      onMarkupChange(
                        productId,
                        product.markupType === 'NONE' ? 'AMOUNT' : product.markupType,
                        val ?? 0,
                      )
                    }
                    decimalPlaces={2}
                    min={0}
                    disabled={!editable}
                    sx={compactInputSx}
                  />
                </>
              )}

              {showLineDiscountControls && (
                <>
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ ...controlLabelSx, ml: showLineMarkupControls ? 0.5 : 0 }}
                  >
                    Descuento
                  </Typography>
                  <AdjustmentTypeToggle
                    value={product.discountType === 'PERCENTAGE' ? 'PERCENTAGE' : 'AMOUNT'}
                    disabled={!editable}
                    onChange={(type) => onDiscountChange(productId, type, product.discountValue)}
                    ariaLabel="Tipo de descuento de la pieza"
                  />
                  <DecimalNumberInput
                    size="small"
                    value={product.discountValue > 0 ? product.discountValue : ''}
                    onChange={(val) => {
                      const numValue = val ?? 0;
                      let validatedValue = numValue;
                      if (product.discountType === 'PERCENTAGE') {
                        validatedValue = Math.min(numValue, 99);
                      } else if (product.discountType === 'AMOUNT') {
                        validatedValue = Math.min(numValue, product.adjustedPrice);
                      }
                      onDiscountChange(
                        productId,
                        product.discountType === 'NONE' ? 'AMOUNT' : product.discountType,
                        validatedValue,
                      );
                    }}
                    decimalPlaces={2}
                    min={0}
                    max={product.discountType === 'PERCENTAGE' ? 99 : product.adjustedPrice}
                    disabled={!editable}
                    error={discountInvalid}
                    sx={compactInputSx}
                  />
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </S.PricingLineCard>
  );
};

export default SalesLinePricingCard;
